<script setup lang="ts">
const { t, locale, locales, setLocale } = useI18n()
const switchLocalePath = useSwitchLocalePath()
const localeObject = useLocaleObject()

const showCountrySelectBottomSheet = ref(false)

const availableLocales = computed(() => {
  const sortedLocales = sortLocalesByMultiLevelProximity(locales.value, localeObject.value.country)

  return sortedLocales.map((l) => {
    const displayNames = new Intl.DisplayNames([l.language], { type: 'region' })

    const formattedNumber = new Intl.NumberFormat(l.language, {
      style: 'currency',
      currency: l.currency,
    }).format(0)

    const nativeName = displayNames.of(l.country)
    const localCurrencySymbol = formattedNumber.replace(/[\d.,\s]/g, '')

    return {
      ...l,
      countryLabel: nativeName,
      label: `${nativeName} (${localCurrencySymbol})`,
      shortLabel: `${l.country} (${localCurrencySymbol})`,
    }
  })
})

const selectedLocaleObject = computed(() => availableLocales.value.find(l => l.code === locale.value)!)

async function handleClick(l: typeof locale.value) {
  await setLocale(l)
  switchLocalePath(l)
  window.location.reload()
}
</script>

<template>
  <div
    role="region"
    :aria-label="t('language_and_country_change')"
    class="relative cursor-pointer"
  >
    <LazyUiDrawer>
      <LazyUiDrawerTrigger as-child>
        <UiButton
          variant="outline"
          data-testid="languageCountrySwitch"
        >
          <div class="flex items-center">
            <Icon
              :name="`i-${selectedLocaleObject.flag}`"
              alt="flag"
              class="mr1 aspect-5/4 w5 rd-0 rounded-full align-bottom shadow-sm"
              data-testid="languageCountrySwitchSelectedCountryFlag"
            />
            <span :data-testid="`languageCountrySwitchSelectedCountry_${selectedLocaleObject.country}`" class="text-xs fw-600 leading-none">
              {{ selectedLocaleObject.shortLabel }}
            </span>
          </div>
        </UiButton>
      </LazyUiDrawerTrigger>
      <LazyUiDrawerContent>
        <LazyUiDrawerHeader>
          <LazyUiDrawerTitle class="text-center">
            {{ t('form.country.label') }}
          </LazyUiDrawerTitle>
        </LazyUiDrawerHeader>

        <LazyUiDrawer>
          <LazyUiDrawerTrigger as-child>
            <button
              class="cursor-pointer border-none bg-none px4 py6 outline-none"
              data-testid="languageCountrySwitchCountriesOpener"
              @click="showCountrySelectBottomSheet = true"
            >
              <div
                class="wfull flex items-center justify-between"
                data-testid="languageCountrySwitchCountriesOpenerContent"
              >
                <span class="mb0.5 text-sm fw600">
                  {{ t('current_store') }}
                </span>
                <div class="flex items-center">
                  <Icon
                    :name="selectedLocaleObject.flag!"
                    class="mb1 mr2 aspect-5/4 h4 w4 rd-0 align-middle"
                  />
                  <span
                    :data-testid="`languageCountrySwitchCountriesOpenerContentLabel-${selectedLocaleObject.country}`"
                    class="mb0.5 text-sm fw600"
                  >
                    {{ selectedLocaleObject.label }}
                  </span>

                  <Icon name="i-ri:arrow-right-s-line" class="ml3 text-lg text-slate-400" />
                </div>
              </div>
            </button>
          </LazyUiDrawerTrigger>
          <LazyUiDrawerContent class="max-h-[85vh] flex flex-col">
            <LazyUiDrawerHeader class="flex-shrink-0">
              <UiDrawerTitle>
                {{ t('select_a_country') }}
              </UiDrawerTitle>
            </LazyUiDrawerHeader>

            <div class="flex-1 overflow-y-auto overscroll-contain scroll-smooth">
              <div class="wfull flex flex-col bg-white">
                <a
                  v-for="loc in availableLocales" :key="loc.code"
                  :href="`/${loc.code}`"
                  :data-testid="`languageCountrySwitchCountry-${loc.country}`"
                  class="box-border h14 wfull flex shrink-0 cursor-pointer items-center border-t n-border-base bg-white px4 leading-none hover:bg-slate-50"
                  target="_self"
                  rel="noreferrer"
                  @click.prevent="handleClick(loc.code)"
                >
                  <Icon
                    :name="loc.flag!"
                    class="mr2 aspect-5/4 h4 w4 rd-0 align-bottom"
                  />
                  <span data-testid="languageCountrySwitchCountryLabel">
                    {{ loc.label }}
                  </span>

                  <Icon
                    v-if="loc.code === localeObject.code"
                    name="i-ri:check-line"
                    class="ml-auto h5 w5"
                  />
                </a>
              </div>
            </div>
          </LazyUiDrawerContent>
        </LazyUiDrawer>
      </LazyUiDrawerContent>
    </LazyUiDrawer>
  </div>
</template>
